import React, { useEffect, useMemo, useRef } from 'react';
import { TooltipNew, Icon, Button, Text } from '@msc-b2c/shared-components';
import { DrawerProps } from '@msc-b2c/interfaces/types';
import {
  defaultTooltipsVisibility,
  formatPrice,
  getAvailablePrice,
  getBeforePromotionPrice,
  getCurrencySymbol,
  getPriceCurrencyOrder,
  isPromotionPriceAvailable,
  isObsDrawerPromoPriceAvailable,
  managePlurals,
  replacePlaceholders,
  RootState,
  roundPrice,
  toggleDisplayOverlay,
} from '@msc-b2c/shared-utils';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import { isCountDownDays, isCountDownHours, getPromoStatus } from '../utils/pillManagementUtils';
import { navigateOrRedirectToRoute } from '../navigationmenu/utils/navigationMenuUtils';
import { useNavigate } from 'react-router-dom';

import styles from './drawer.module.scss';
import { OBSTypesEnum } from '@msc-b2c/interfaces/enums';
import { useFocusTrap } from './useFocusTrap';
import { getLinkedMinorObs } from '../postbookingresults/utils/allInclusiveCmsHelpers';

export const Drawer: React.FC<DrawerProps> = ({
  postBookingWrapperID = '',
  title,
  titleIcon,
  closeIcon,
  firstButtonLabel,
  secondButtonLabel,
  secondButtonDisabled = false,
  processingFirstButtonLabel,
  processingFirstButtonIcon,
  limitedSeatsPlaceholder,
  alreadyInCartMessageLabel,
  alreadyInCartCtaLabel,
  alreadyInCartCtaLink,
  isDrawerOpen,
  enableClosingByClickingOutside,
  onClose,
  onApply,
  onClear,
  children,
  stylingOption,
  disabled = false,
  isProcessing = false,
  hiddenFooter = false,
  footerButtonsVariant = 'default',
  disableAllFooterButtons = false,
  showPriceInFooter,
  footerDiningStyle,
  diningCartMultistepFlow = false,
}) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const scrollPositionRef = useRef<{ scrollY: number; href: string } | null>(null);
  useFocusTrap(dialogRef, Boolean(isDrawerOpen));

  const drawerPrice = useSelector((state: RootState) => state.myAreaDrawers.drawerPrice);
  const currency = getCurrencySymbol();
  const selectedExcursionCard = useSelector(
    (state: RootState) => state.myAreaDrawers.selectedMyAreaExcursionCard
  );
  const selectedOBSCard = useSelector(
    (state: RootState) => state.myAreaDrawers.selectedMyAreaOBSCard
  );
  const allOBS = useSelector((state: RootState) => state.myCruise.obs?.onboardServices);
  const allInclusiveCmsItems = useSelector(
    (state: RootState) => state.myCruise.allInclusiveCmsItems
  );
  const promotionPriceEnabled = useSelector(
    (state: RootState) => state.myAreaCommonConfig.prePaidCardProps.promotionPriceEnabled
  );
  const limitedSeatsPlaceholderThreshold = useSelector(
    (state: RootState) => state.myAreaCommonConfig.drawerProps.limitedSeatsPlaceholderThreshold
  );
  const closeDrawerLabel = useSelector(
    (state: RootState) => state.myAreaCommonConfig.drawerProps.closeDrawerLabel
  );
  const tooltipLabel = useSelector(
    (state: RootState) => state.myAreaCommonConfig.drawerProps.tooltipLabel
  );
  const isLayerExcursionOpen = useSelector(
    (state: RootState) => state.myAreaDrawers.isLayerMyAreaExcursionOpen
  );
  const linkedMinorObsCard = useMemo(
    () =>
      !isLayerExcursionOpen && selectedOBSCard
        ? getLinkedMinorObs(selectedOBSCard, allOBS, allInclusiveCmsItems)
        : undefined,
    [isLayerExcursionOpen, selectedOBSCard, allOBS, allInclusiveCmsItems]
  );
  const tooltipsVisibilityConfig = useSelector(
    (state: RootState) => state.myAreaCommonConfig.tooltipsVisibility[postBookingWrapperID]
  );

  const isPromoTooltipEnabled = (tooltipsVisibilityConfig ?? defaultTooltipsVisibility)
    ?.promoTooltipEnabled;

  const availableSeats = selectedExcursionCard?.inventory?.numberAvailable;
  const { pillsVisibility } = useSelector((state: RootState) => state.myAreaCommonConfig);
  const isPillExist = useSelector(
    (state: RootState) => state.myAreaCommonConfig.drawerProps.drawerPillConfigurations
  );

  const isCountDownPillExist = isPillExist?.some(
    (item) => item.pillType === 'countDownHours' || item.pillType === 'countDownDays'
  );

  const countDownHoursLabel =
    isCountDownPillExist && isPillExist
      ? isPillExist.find((item) => item.pillType === 'countDownHours')?.pillLabel
      : '';

  const countDownDaysLabel =
    isCountDownPillExist && isPillExist
      ? isPillExist.find((item) => item.pillType === 'countDownDays')?.pillLabel
      : '';

  const shouldShowCountdown =
    isLayerExcursionOpen && selectedExcursionCard
      ? isCountDownHours(selectedExcursionCard, pillsVisibility, 'countDownHours') ||
        isCountDownDays(selectedExcursionCard, pillsVisibility, 'countDownDays')
      : !isLayerExcursionOpen && selectedOBSCard
      ? isCountDownHours(selectedOBSCard, pillsVisibility, 'countDownHours') ||
        isCountDownDays(selectedOBSCard, pillsVisibility, 'countDownDays')
      : false;

  const promoStatus =
    isLayerExcursionOpen && selectedExcursionCard
      ? getPromoStatus(selectedExcursionCard, pillsVisibility)
      : !isLayerExcursionOpen && selectedOBSCard
      ? getPromoStatus(selectedOBSCard, pillsVisibility)
      : null;

  const getCountDownLabel: string = promoStatus
    ? shouldShowCountdown
      ? replacePlaceholders(managePlurals(promoStatus.dayFromEndDate, countDownDaysLabel), {
          remaining_days: promoStatus.dayFromEndDate.toString(),
        })
      : replacePlaceholders(
          managePlurals(Math.max(1, promoStatus.timeDiffHours), countDownHoursLabel),
          { remaining_hours: Math.max(1, promoStatus.timeDiffHours).toString() }
        )
    : '';

  const CountDownLabel = (label: string): string => {
    if (!label) return '';
    const words = label.toLowerCase().trim().split(' ');
    words[0] = words[0].charAt(0).toUpperCase() + words[0].slice(1);
    return words.join(' ');
  };

  const promo = isLayerExcursionOpen
    ? selectedExcursionCard?.promotion
    : selectedOBSCard?.promotion;

  const isPromoPriceAvailable = isLayerExcursionOpen
    ? isPromotionPriceAvailable(
        promotionPriceEnabled,
        drawerPrice,
        promo?.validFrom,
        promo?.validTo
      )
    : isObsDrawerPromoPriceAvailable(
        promotionPriceEnabled,
        drawerPrice,
        selectedOBSCard?.promotion,
        linkedMinorObsCard?.promotion
      );

  const showLimitedSeatsLabel =
    availableSeats &&
    limitedSeatsPlaceholderThreshold &&
    Number.parseInt(availableSeats) < limitedSeatsPlaceholderThreshold;

  const limitedSeatsLabel =
    showLimitedSeatsLabel &&
    selectedExcursionCard?.activityDetails?.isLimitedSeats &&
    limitedSeatsPlaceholder
      ? replacePlaceholders(limitedSeatsPlaceholder, {
          remainingSeats: availableSeats,
        })
      : '';

  useEffect(() => {
    if (isDrawerOpen) {
      scrollPositionRef.current = {
        scrollY: window.scrollY,
        href: window.location.href,
      };
    }

    return () => {
      const scrollPosition = scrollPositionRef.current;
      scrollPositionRef.current = null;

      if (!scrollPosition || scrollPosition.href !== window.location.href) return;

      requestAnimationFrame(() =>
        window.scrollTo({ top: scrollPosition.scrollY, behavior: 'instant' })
      );
    };
  }, [isDrawerOpen]);

  useEffect(() => {
    toggleDisplayOverlay(isDrawerOpen);
    return () => {
      toggleDisplayOverlay(false);
    };
  }, [isDrawerOpen]);

  const isCartCtaOverride = Boolean(alreadyInCartCtaLabel);
  const showAlreadyInCartMessage = Boolean(alreadyInCartMessageLabel);
  const isDining = selectedOBSCard?.type.code === OBSTypesEnum.RESTAURANTS;
  const isDiningCartDrawer = isDining && !!secondButtonLabel;
  const isTransparentHeader = stylingOption?.includes('transparent-header');
  const isConfirmationDrawer = stylingOption?.includes('confirmation-drawer');
  const isRefundDrawer = stylingOption?.includes('-refund');
  const shouldShowPriceSection = showPriceInFooter ?? isTransparentHeader;

  // Confirmation drawer: clear focus so the secondary CTA does not keep :focus styles
  // (same as hover in our button mixin) after the primary CTA was clicked.
  useEffect(() => {
    if (!isDrawerOpen || !isConfirmationDrawer) {
      return;
    }
    requestAnimationFrame(() => {
      (document.activeElement as HTMLElement | null)?.blur?.();
    });
  }, [isDrawerOpen, isConfirmationDrawer]);

  const navigate = useNavigate();
  const handleFooterCtaClick = () => {
    if (alreadyInCartCtaLink) {
      toggleDisplayOverlay(false);
      onClose();
      navigateOrRedirectToRoute(alreadyInCartCtaLink ?? {}, navigate);
    }
  };

  useEffect(() => {
    if (enableClosingByClickingOutside) {
      const handleOutsideClick = (event: MouseEvent) => {
        const drawerElement = document.querySelector('.drawer__container');
        if (isDrawerOpen && drawerElement && !drawerElement.contains(event.target as Node)) {
          onClose();
        }
      };

      document.addEventListener('mousedown', handleOutsideClick);

      return () => {
        document.removeEventListener('mousedown', handleOutsideClick);
      };
    }
  }, [isDrawerOpen, enableClosingByClickingOutside, onClose]);

  const renderPromoTooltip = (): JSX.Element | null => {
    if (
      isPromoTooltipEnabled &&
      isPromoPriceAvailable &&
      (!!promo?.description || !!promo?.webDescription)
    ) {
      return (
        <TooltipNew
          appliedCssClassNames="default-text"
          triggerAriaLabel={tooltipLabel}
          content={
            <>
              {!!promo?.description && (
                <Text
                  text={promo.description}
                  styleOption={'body-xsmall'}
                />
              )}
              {!!promo?.webDescription && promo?.description !== promo.webDescription && (
                <Text
                  text={promo.webDescription}
                  styleOption={'body-xsmall'}
                />
              )}
            </>
          }
          positionStyle="top"
        >
          <div>
            <Icon
              icon={'info-outline'}
              ariaHidden={true}
              styleOption={styles.iconPosition}
            />
          </div>
        </TooltipNew>
      );
    }

    return null;
  };

  const getSecondButtonType = (): string => {
    if (isRefundDrawer && isConfirmationDrawer) return 'cmp-button__secondary';

    return isDiningCartDrawer || footerButtonsVariant === 'secondary'
      ? 'cmp-button__secondary'
      : 'cmp-button__link';
  };

  /** Main footer CTA (firstButtonLabel). Cancel/secondary action uses its own styleOption on the other Button. */
  const getButtonType = (): string => {
    if (isRefundDrawer && isTransparentHeader) return 'cmp-button__secondary';

    return isCartCtaOverride ? 'cmp-button__secondary' : 'cmp-button__primary';
  };

  const showPriceBlock =
    shouldShowPriceSection &&
    !isCartCtaOverride &&
    drawerPrice &&
    (isTransparentHeader || showPriceInFooter);

  const primaryFooterButton = (
    <Button
      text={
        isCartCtaOverride
          ? alreadyInCartCtaLabel
          : isProcessing
          ? processingFirstButtonLabel
          : firstButtonLabel
      }
      icon={isCartCtaOverride ? '' : isProcessing ? processingFirstButtonIcon : ''}
      onClick={isCartCtaOverride ? handleFooterCtaClick : onApply}
      disabled={(isCartCtaOverride ? false : disabled) || disableAllFooterButtons}
      styleOption={`${
        isTransparentHeader
          ? footerButtonsVariant === 'secondary'
            ? getButtonType()
            : getButtonType() + ' -extended -prepaid'
          : getButtonType() + ' -filters'
      } ${footerDiningStyle === '-dining-default' ? ' -extended' : ''}`}
    />
  );

  const secondaryFooterButton = secondButtonLabel ? (
    <Button
      text={secondButtonLabel}
      onClick={onClear}
      styleOption={getSecondButtonType()}
      disabled={secondButtonDisabled || disableAllFooterButtons}
    />
  ) : null;

  const footerInfoSection =
    showAlreadyInCartMessage || showPriceBlock ? (
      <div
        className={clsx(styles.priceSection, {
          processing: isProcessing,
        })}
      >
        {showAlreadyInCartMessage && (
          <Text
            text={alreadyInCartMessageLabel ?? ''}
            styleOption={'body-default'}
          />
        )}
        {showPriceBlock && (
          <>
            <div className={styles['drawer__container__price']}>
              {renderPromoTooltip()}
              <Text
                text={replacePlaceholders(getPriceCurrencyOrder(), {
                  currency: currency,
                  price: formatPrice(roundPrice(getAvailablePrice(drawerPrice))),
                })}
                styleOption={clsx('header-m', {
                  'drawer__container__price-after-discount': isPromoPriceAvailable,
                })}
              />
              {isPromoPriceAvailable && (
                <Text
                  text={replacePlaceholders(getPriceCurrencyOrder(), {
                    currency: currency,
                    price: formatPrice(roundPrice(getBeforePromotionPrice(drawerPrice))),
                  })}
                  styleOption={'header-m drawer__container__price-before-discount'}
                />
              )}
            </div>
            <>
              {isLayerExcursionOpen && !shouldShowCountdown && (
                <Text
                  text={limitedSeatsLabel}
                  styleOption={`cmp-text__normal ${styles.limitedSeatsLabel}`}
                  tagHtml="span"
                />
              )}

              {shouldShowCountdown && (
                <Text
                  text={CountDownLabel(getCountDownLabel)}
                  styleOption={`cmp-text__normal ${styles.limitedSeatsLabel}`}
                  tagHtml="span"
                />
              )}
            </>
          </>
        )}
      </div>
    ) : null;

  return (
    <>
      {isDrawerOpen && (
        <div
          role="presentation"
          className={styles.drawerBackdrop}
          onClick={enableClosingByClickingOutside ? onClose : undefined}
        />
      )}
      <dialog
        ref={dialogRef}
        tabIndex={-1}
        aria-modal="true"
        aria-label={title || undefined}
        className={clsx(
          'drawer',
          stylingOption,
          isDrawerOpen ? styles['drawer__container'] : styles['drawer__container__hide'],
          { '-dining': isDiningCartDrawer },
          { 'drawer--dining-cart-multistep': diningCartMultistepFlow }
        )}
      >
        {!isTransparentHeader && (
          <div className={`${styles['drawer__container__header']}`}>
            {isConfirmationDrawer && (
              <div className={`${styles['drawer__container__header-title']}`}>
                {titleIcon && <Icon icon={titleIcon ?? ''} />}
                {title && (
                  <Text
                    text={title}
                    styleOption={styles.title}
                  />
                )}
              </div>
            )}
            {title && !isConfirmationDrawer && (
              <Text
                text={title}
                styleOption={styles.title}
              />
            )}
            <Button
              icon={closeIcon ?? 'close'}
              accessibilityLabel={closeDrawerLabel}
              onClick={onClose}
              styleOption={'-no-outline -no-background -drawer-close'}
            />
          </div>
        )}
        {/* Dynamic Content */}
        <div className={styles['drawer__container__content']}>
          {/* Close Button */}
          {isTransparentHeader && (
            <div
              className={`${styles['drawer__container__header']} ${styles['drawer__container__header--transparent']}`}
            >
              {title && (
                <Text
                  text={title}
                  styleOption={styles.title}
                />
              )}
              <Button
                icon={closeIcon ?? 'close'}
                accessibilityLabel={closeDrawerLabel}
                onClick={onClose}
                styleOption={'-no-outline -no-background -drawer-close'}
              />
            </div>
          )}
          {children}
        </div>
        {/* Footer */}
        <div
          className={clsx(
            hiddenFooter ? 'hidden' : '',
            isTransparentHeader
              ? 'drawer__container__footer -prepaid'
              : 'drawer__container__footer',
            {
              'secondary-footer-layout': footerButtonsVariant === 'secondary',
              [styles['drawer__container__footer--secondary-layout']]:
                footerButtonsVariant === 'secondary',
              [styles['drawer__container__footer--single-action']]: !secondButtonLabel && isDining,
              '-dining': isDining || isDiningCartDrawer,
              [footerDiningStyle ?? '']: !!footerDiningStyle,
            }
          )}
        >
          {secondaryFooterButton}
          {footerInfoSection}
          {primaryFooterButton}
        </div>
      </dialog>
    </>
  );
};
